import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useMyInstalledAgents } from "@/features/marketplace/hooks/useMarketplace";
import { useTeamSessions } from "@/features/team-sessions/hooks/useTeamSessions";

export interface TaskFormValue {
  targetKind: "agent" | "team";
  agentId: string;
  teamSessionId: string;
  title: string;
  prompt: string;
  /** String do input number; vazio = sem limite. */
  maxTokens: string;
}

export const EMPTY_TASK_FORM: TaskFormValue = {
  targetKind: "agent",
  agentId: "",
  teamSessionId: "",
  title: "",
  prompt: "",
  maxTokens: "",
};

/**
 * Valida o form; retorna a CHAVE i18n (namespace ``tasks``) do erro ou
 * null quando valido. Exportado para reuso no scheduler e em testes.
 */
export function validateTaskForm(value: TaskFormValue): string | null {
  if (!value.title.trim()) return "form.errors.titleRequired";
  if (!value.prompt.trim()) return "form.errors.promptRequired";
  if (value.targetKind === "agent" && !value.agentId) {
    return "form.errors.agentRequired";
  }
  if (value.targetKind === "team" && !value.teamSessionId) {
    return "form.errors.teamSessionRequired";
  }
  if (value.maxTokens) {
    const n = Number(value.maxTokens);
    if (!Number.isInteger(n) || n < 1) {
      return "form.errors.maxTokensInvalid";
    }
  }
  return null;
}

/** Monta o payload de alvo + limite a partir do form validado. */
export function taskFormToPayload(value: TaskFormValue): {
  title: string;
  prompt: string;
  agent_id?: string;
  team_session_id?: string;
  max_tokens?: number;
} {
  return {
    title: value.title.trim(),
    prompt: value.prompt.trim(),
    ...(value.targetKind === "agent"
      ? { agent_id: value.agentId }
      : { team_session_id: value.teamSessionId }),
    ...(value.maxTokens ? { max_tokens: Number(value.maxTokens) } : {}),
  };
}

interface Props {
  value: TaskFormValue;
  onChange: (value: TaskFormValue) => void;
}

/**
 * Campos compartilhados entre "Iniciar task" e o editor de
 * agendamento: alvo (agente instalado OU sessao em time existente),
 * titulo, prompt e limite de tokens.
 */
export function TaskForm({ value, onChange }: Props) {
  const { t } = useTranslation("tasks");
  const agentsQuery = useMyInstalledAgents();
  const sessionsQuery = useTeamSessions();

  function patch(partial: Partial<TaskFormValue>) {
    onChange({ ...value, ...partial });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="task-title">{t("form.title")}</Label>
        <Input
          id="task-title"
          value={value.title}
          maxLength={200}
          placeholder={t("form.titlePlaceholder")}
          onChange={(e) => patch({ title: e.target.value })}
        />
      </div>

      <Tabs
        value={value.targetKind}
        onValueChange={(v) => patch({ targetKind: v as "agent" | "team" })}
      >
        <TabsList>
          <TabsTrigger value="agent">{t("form.targetAgent")}</TabsTrigger>
          <TabsTrigger value="team">{t("form.targetTeam")}</TabsTrigger>
        </TabsList>
        <TabsContent value="agent" className="pt-2">
          <Select
            value={value.agentId}
            onValueChange={(v) => patch({ agentId: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("form.agentPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {(agentsQuery.data ?? []).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {agentsQuery.data?.length === 0 ? (
            <p className="mt-1.5 text-xs text-ink-500">
              {t("form.noInstalledAgents")}{" "}
              <Link to="/marketplace" className="text-brand-600 underline">
                {t("form.goToMarketplace")}
              </Link>
            </p>
          ) : null}
        </TabsContent>
        <TabsContent value="team" className="pt-2">
          <Select
            value={value.teamSessionId}
            onValueChange={(v) => patch({ teamSessionId: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("form.teamSessionPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {(sessionsQuery.data ?? []).map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sessionsQuery.data?.length === 0 ? (
            <p className="mt-1.5 text-xs text-ink-500">
              {t("form.noTeamSessions")}{" "}
              <Link to="/sessions/new" className="text-brand-600 underline">
                {t("form.goToNewSession")}
              </Link>
            </p>
          ) : null}
        </TabsContent>
      </Tabs>

      <div className="space-y-1.5">
        <Label htmlFor="task-prompt">{t("form.prompt")}</Label>
        <Textarea
          id="task-prompt"
          rows={8}
          value={value.prompt}
          placeholder={t("form.promptPlaceholder")}
          onChange={(e) => patch({ prompt: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="task-max-tokens">{t("form.maxTokens")}</Label>
        <Input
          id="task-max-tokens"
          type="number"
          min={1}
          value={value.maxTokens}
          placeholder={t("form.maxTokensPlaceholder")}
          onChange={(e) => patch({ maxTokens: e.target.value })}
        />
        <p className="text-xs text-ink-500">{t("form.maxTokensHint")}</p>
      </div>
    </div>
  );
}
